import React, { useEffect, useState } from 'react';
import { Search, Users, AlertTriangle, ChevronRight } from 'lucide-react';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { clientsApi } from '../lib/api';

const ClientRow = ({ c, onOpen }) => (
  <button type="button" onClick={() => onOpen(c.id)} data-testid={`client-row-${c.id}`}
    className="w-full text-left flex items-center gap-4 p-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold shrink-0">
      {(c.first_name || '?').charAt(0).toUpperCase()}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-bold text-slate-900">{c.first_name} {c.last_name}</p>
        {c.possible_duplicates?.length > 0 && (
          <span data-testid={`duplicate-badge-${c.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle className="w-3 h-3" />Doublon possible
          </span>
        )}
      </div>
      <p className="text-sm text-slate-500 truncate">{[c.emails?.[0], c.phones?.[0]].filter(Boolean).join(' · ')}</p>
    </div>
    <span className="text-sm text-slate-600 whitespace-nowrap">{c.quote_count} devis</span>
    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
  </button>
);

const ClientsAdmin = ({ onOpenClient, refreshKey }) => {
  const [query, setQuery] = useState('');
  const [onlyDuplicates, setOnlyDuplicates] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await clientsApi.list({ q: query, duplicates: onlyDuplicates });
        setClients(res.data);
      } catch { toast.error('Erreur de chargement des clients'); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [query, onlyDuplicates, refreshKey]);

  return (
    <div className="space-y-4" data-testid="clients-admin">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nom, e-mail ou téléphone…"
            className="pl-9 bg-white" data-testid="clients-search-input" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-md px-3 h-10 cursor-pointer">
          <input type="checkbox" checked={onlyDuplicates} onChange={(e) => setOnlyDuplicates(e.target.checked)} data-testid="clients-duplicates-filter" />
          Doublons possibles uniquement
        </label>
      </div>
      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="clients-list">
        <div className="flex items-center gap-2 p-4 border-b border-slate-200">
          <Users className="w-4 h-4 text-slate-500" />
          <h2 className="font-bold text-slate-900">Clients <span className="text-slate-400 font-normal" data-testid="clients-count">({clients.length})</span></h2>
        </div>
        {!loading && clients.length === 0 && <p className="p-6 text-sm text-slate-500" data-testid="clients-empty">Aucun client trouvé.</p>}
        {clients.map((c) => <ClientRow key={c.id} c={c} onOpen={onOpenClient} />)}
      </section>
    </div>
  );
};

export default ClientsAdmin;
